import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  allMovies,
  categories,
  getTopRatedMovies,
  searchMovies,
} from "@/lib/movies";

const hotKeywords = ["婚姻", "都市", "悬疑", "家庭", "海边", "复仇", "职场"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [focused, setFocused] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const results = useMemo(() => searchMovies(keyword), [keyword]);
  const suggestions = useMemo(() => {
    if (!input.trim() || input.trim() === keyword) return [];
    return searchMovies(input).slice(0, 6);
  }, [input, keyword]);
  const hotMovies = getTopRatedMovies(5);

  usePageMeta({
    title: keyword
      ? `搜索「${keyword}」 - 韩国伦理电影在线观看`
      : "影片搜索 - 韩国伦理电影在线观看",
    description: keyword
      ? `韩国伦理电影在线观看站内搜索「${keyword}」的结果，共找到 ${results.length} 部相关影片，可按片名、类型与关键词浏览。`
      : "输入片名、类型或关键词，快速检索韩国伦理电影资料库中的影片，支持剧情、爱情、社会等分类关键词搜索。",
    keywords: "韩国伦理电影搜索,伦理电影在线观看,韩国电影观看,影片检索",
  });

  const submit = (value: string) => {
    const next = value.trim();
    if (next) {
      setSearchParams({ q: next });
    } else {
      setSearchParams({});
    }
    setFocused(false);
  };

  return (
    <Layout>
      <div className="bg-background-100/60 border-b border-background-200/70">
        <div className="px-4 md:px-8 lg:px-12 pt-8 pb-10">
          <div className="max-w-shell mx-auto">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
            <p className="mt-5 font-label text-xs tracking-[0.32em] text-primary-500 mb-2">
              SEARCH THE ARCHIVE
            </p>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 font-bold">搜索影片</h1>
            <p className="mt-2 text-sm text-foreground-600">
              支持按片名、类型、导演、主演与标签关键词检索，全部在前端完成匹配。
            </p>

            <div ref={boxRef} className="relative mt-6 max-w-2xl">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  submit(input);
                }}
              >
                <div className="flex h-12 rounded-md border border-background-300 bg-background-50 overflow-hidden focus-within:border-primary-400 transition-colors">
                  <span className="w-11 flex items-center justify-center text-foreground-500 shrink-0">
                    <i className="ri-search-line text-lg" aria-hidden="true"></i>
                  </span>
                  <input
                    type="text"
                    value={input}
                    onChange={(event) => {
                      setInput(event.target.value);
                      setFocused(true);
                    }}
                    onFocus={() => setFocused(true)}
                    placeholder="输入片名或关键词，例如「婚姻」「海边」「无声的潮汐」"
                    aria-label="搜索影片关键词"
                    className="flex-1 min-w-0 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
                  />
                  {input && (
                    <button
                      type="button"
                      onClick={() => setInput("")}
                      className="w-9 flex items-center justify-center text-foreground-500 hover:text-foreground-900 transition-colors cursor-pointer"
                      aria-label="清空输入"
                    >
                      <i className="ri-close-line" aria-hidden="true"></i>
                    </button>
                  )}
                  <button
                    type="submit"
                    className="h-12 px-5 bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    搜索
                  </button>
                </div>
              </form>

              {focused && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 z-30 rounded-md border border-background-200 bg-background-50 overflow-hidden animate-fade-in">
                  <p className="px-4 pt-3 pb-1.5 text-[11px] text-foreground-500">联想建议</p>
                  {suggestions.map((movie) => (
                    <button
                      key={movie.id}
                      type="button"
                      onClick={() => {
                        setInput(movie.title);
                        submit(movie.title);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true"></i>
                      <span className="text-sm text-foreground-900 flex-1 truncate">{movie.title}</span>
                      <span className="text-[11px] text-foreground-500 shrink-0">
                        {categories.find((item) => item.slug === movie.type)?.name} · {movie.year}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">热门关键词</span>
              {hotKeywords.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => {
                    setInput(word);
                    submit(word);
                  }}
                  className="h-7 px-3 rounded-full bg-secondary-100/70 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 py-10 md:py-12">
        <div className="max-w-shell mx-auto">
          {keyword ? (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-6">
                <h2 className="font-heading text-lg md:text-xl text-foreground-950 font-semibold">
                  「{keyword}」的搜索结果
                </h2>
                <p className="text-sm text-foreground-500">
                  共找到 <span className="text-primary-500 font-medium">{results.length}</span> 部影片
                </p>
              </div>

              {results.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
                  {results.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} compact />
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-background-300/80 bg-background-100/40 px-6 py-14 text-center">
                  <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center mx-auto">
                    <i className="ri-search-eye-line text-3xl" aria-hidden="true"></i>
                  </span>
                  <h3 className="font-heading text-lg text-foreground-950 font-semibold mt-5">
                    没有找到与「{keyword}」相关的影片
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    试试更短的关键词，或者从下方热门影片中挑选一部。
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {hotKeywords.map((word) => (
                      <button
                        key={word}
                        type="button"
                        onClick={() => {
                          setInput(word);
                          submit(word);
                        }}
                        className="h-8 px-3 rounded-full border border-background-300 text-xs text-foreground-700 hover:border-primary-400 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {word}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-background-200/70 bg-background-100/40 px-6 py-14 text-center">
              <span className="w-16 h-16 rounded-full bg-primary-500/15 text-primary-400 flex items-center justify-center mx-auto">
                <i className="ri-search-line text-3xl" aria-hidden="true"></i>
              </span>
              <h2 className="font-heading text-lg text-foreground-950 font-semibold mt-5">
                输入关键词开始搜索
              </h2>
              <p className="mt-2 text-sm text-foreground-600 max-w-md mx-auto leading-relaxed">
                可以搜索片名、类型名称或影片标签。片库目前收录 {allMovies.length} 部韩国伦理电影资料。
              </p>
              <Link
                to="/category/all"
                className="mt-6 inline-flex h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm items-center gap-2 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                直接浏览全部片库
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          )}

          <section className="mt-12 pt-8 border-t border-background-200/60">
            <h2 className="font-heading text-base text-foreground-950 font-semibold mb-4">热门影片推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} compact />
              ))}
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}